import React, { useState } from 'react';
import { getProfileCompanyPicUrl } from '../../helpers/url_helper';
import { dataAPI } from '../../helpers/api_service';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { TextField } from '@mui/material';

const UserProfile = ({ userData, mode, onUserDataUpdate }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [selectedProfilePic, setSelectedProfilePic] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentUserData, setCurrentUserData] = useState(userData);
  const [formErrors, setFormErrors] = useState({});
  const [showValidation, setShowValidation] = useState(false);

  // Helper function to format role name from camelCase to proper case
  const formatRoleName = (roleName) => {
    if (!roleName) return 'User Profile';
    
    // Convert camelCase to proper case with spaces
    return roleName
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
      .trim() + ' Profile'; // Add "Profile" suffix
  };

  // Helper function to handle profile picture URL
  const getProfilePicUrl = (profilePic) => {
    if (!profilePic) return '';

    // Check if URL already starts with http (any protocol)
    if (profilePic.toLowerCase().startsWith('http://') || profilePic.toLowerCase().startsWith('https://')) {
      return profilePic; // Return as is if it's already a full URL
    } else {
      return getProfileCompanyPicUrl(profilePic); // Append profile/company pic URL prefix if it's a relative path
    }
  };

  // Validation functions
  const validateUsername = (username) => {
    if (!username) return 'Username is required';
    if (username.length < 3) return 'Username must be at least 3 characters long';
    if (username.length > 30) return 'Username must be less than 30 characters';
    if (!/^[a-zA-Z0-9._]+$/.test(username)) return 'Username can only contain letters, numbers, underscores, and dots';
    if (username.startsWith('.') || username.endsWith('.')) return 'Username cannot start or end with a dot';
    if (username.includes('..')) return 'Username cannot contain consecutive dots';
    return null; // No error
  };

  const validateEmail = (email) => {
    if (!email) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    return null; // No error
  };

  const validateFullName = (fullName) => {
    if (!fullName) return 'Full name is required';
    if (fullName.length < 2) return 'Full name must be at least 2 characters long';
    if (fullName.length > 50) return 'Full name must be less than 50 characters';
    return null; // No error
  };

  const validateBirthdate = (birthdate) => {
    if (!birthdate) return null; // Optional field
    const birthDate = new Date(birthdate);
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    // Adjust age if birthday hasn't occurred this year
    const actualAge = monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate()) 
      ? age - 1 
      : age;
    
    if (actualAge < 18) return 'You must be at least 18 years old';
    if (actualAge > 100) return 'Please enter a valid birthdate';
    return null; // No error
  };

  // Check if form has any errors
  const hasFormErrors = () => {
    let errors = {};
    
    const usernameError = validateUsername(editData.username);
    if (usernameError) errors.username = usernameError;
    
    const emailError = validateEmail(editData.email);
    if (emailError) errors.email = emailError;
    
    const fullNameError = validateFullName(editData.full_name);
    if (fullNameError) errors.full_name = fullNameError;
    
    const birthdateError = validateBirthdate(editData.birthdate);
    if (birthdateError) errors.birthdate = birthdateError;
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEdit = () => {
    if (!isEditMode) {
      // Entering edit mode - populate edit data
      setEditData({
        username: currentUserData.username || '',
        full_name: currentUserData.full_name || '',
        email: currentUserData.email || '',
        birthdate: currentUserData.birthdate || ''
      });
    }
    setIsEditMode(!isEditMode);
  };

  // Helper function to update session data
  const updateSessionData = (updatedUserData) => {
    // Update localStorage with complete user data
    localStorage.setItem('userData', JSON.stringify(updatedUserData));
    
    // Update individual session fields for topbar components
    localStorage.setItem('userFullName', updatedUserData.full_name || '');
    localStorage.setItem('userProfilePic', updatedUserData.profile_pic || '');
    
    // Trigger a custom event to notify other components of the update
    window.dispatchEvent(new CustomEvent('userProfileUpdated', {
      detail: updatedUserData
    }));
  };

  const handleSave = async () => {
    setShowValidation(true);
    
    // Validate form before saving
    if (!hasFormErrors()) {
      return;
    }
    
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('username', editData.username);
      formData.append('email', editData.email);
      formData.append('full_name', editData.full_name);
      formData.append('birthdate', editData.birthdate);
      
      // Only append profile_pic if a new one was selected
      if (selectedProfilePic) {
        formData.append('profile_pic', selectedProfilePic);
      }

      const response = await dataAPI.editUserProfile(formData);
      
      // Debug: Log the response to see the structure
      console.log('User Profile API Response:', response);
      
      // Get the updated profile picture URL from response or keep existing
      let updatedProfilePic = currentUserData.profile_pic;
      if (selectedProfilePic) {
        // Try different possible response structures
        if (response && response.data && response.data.profile_pic) {
          updatedProfilePic = response.data.profile_pic;
        } else if (response && response.profile_pic) {
          updatedProfilePic = response.profile_pic;
        } else if (response && response.data && response.data.user && response.data.user.profile_pic) {
          updatedProfilePic = response.data.user.profile_pic;
        } else {
          // If no new URL in response, use the temporary local URL for immediate preview
          // The server will process the image and the URL will be updated on next page load
          console.log('No new profile picture URL in response, using temporary preview');
          updatedProfilePic = URL.createObjectURL(selectedProfilePic);
        }
      }
      
      // Update the current user data state
      const updatedUserData = {
        ...currentUserData,
        username: editData.username,
        email: editData.email,
        full_name: editData.full_name,
        birthdate: editData.birthdate,
        profile_pic: updatedProfilePic
      };
      
      setCurrentUserData(updatedUserData);
      
      // Update session data
      updateSessionData(updatedUserData);
      
      // Notify parent component if callback provided
      if (onUserDataUpdate) {
        onUserDataUpdate(updatedUserData);
      }
      
      // Exit edit mode
      setIsEditMode(false);
      setSelectedProfilePic(null);
      setShowValidation(false);
      setFormErrors({});
      
    } catch (error) {
      console.error('Error updating user profile:', error);
      alert('Failed to update user profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setEditData({});
    setSelectedProfilePic(null);
    setShowValidation(false);
    setFormErrors({});
  };

  const handleInputChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  const handleProfilePicChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedProfilePic(file);
    }
  };

  return (
    <div style={{
      backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
      borderRadius: '12px',
      padding: '24px',
      marginBottom: '24px',
      border: `1px solid ${mode === 'dark' ? '#444' : '#e0e0e0'}`,
      boxShadow: mode === 'dark' ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
      }}>
        <h2 style={{
          fontSize: '24px',
          fontWeight: '700',
          color: mode === 'dark' ? '#fff' : '#333',
          margin: 0,
          fontFamily: 'Urbanist, Arial, sans-serif'
        }}>
          {formatRoleName(currentUserData.role_name)}
        </h2>
        {!isEditMode ? (
          <button
            onClick={handleEdit}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: 'transparent',
              color: '#26C3C0',
              border: '2px solid #26C3C0',
              borderRadius: '8px',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              fontFamily: 'Urbanist, Arial, sans-serif',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#26C3C0';
              e.target.style.color = '#fff';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'transparent';
              e.target.style.color = '#26C3C0';
            }}
          >
            <EditIcon style={{ fontSize: '16px' }} />
            Edit
          </button>
        ) : (
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={handleSave}
              disabled={isLoading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: isLoading ? '#ccc' : '#26C3C0',
                color: '#fff',
                border: '2px solid #26C3C0',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontFamily: 'Urbanist, Arial, sans-serif',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.target.style.background = '#22B0AD';
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoading) {
                  e.target.style.background = '#26C3C0';
                }
              }}
            >
              <SaveIcon style={{ fontSize: '16px' }} />
              {isLoading ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={handleCancel}
              disabled={isLoading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: 'transparent',
                color: '#666',
                border: '2px solid #666',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontFamily: 'Urbanist, Arial, sans-serif',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.target.style.background = '#666';
                  e.target.style.color = '#fff';
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoading) {
                  e.target.style.background = 'transparent';
                  e.target.style.color = '#666';
                }
              }}
            >
              <CloseIcon style={{ fontSize: '16px' }} />
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Profile Content */}
      <div style={{
        display: 'flex',
        gap: '24px',
        alignItems: 'flex-start'
      }}>
        {/* Profile Picture */}
        <div style={{
          width: '120px',
          height: '120px',
          borderRadius: '50%',
          backgroundImage: selectedProfilePic ? `url(${URL.createObjectURL(selectedProfilePic)})` : (currentUserData.profile_pic ? `url(${getProfilePicUrl(currentUserData.profile_pic)})` : 'none'),
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundColor: !currentUserData.profile_pic && !selectedProfilePic ? '#26C3C0' : 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          border: `3px solid ${mode === 'dark' ? '#444' : '#e0e0e0'}`,
          position: 'relative'
        }}>
          {!currentUserData.profile_pic && !selectedProfilePic && (
            <span style={{
              color: '#fff',
              fontSize: '36px',
              fontWeight: '600',
              fontFamily: 'Urbanist, Arial, sans-serif'
            }}>
              {currentUserData.full_name ? currentUserData.full_name.charAt(0).toUpperCase() : 'U'}
            </span>
          )}
          {isEditMode && (
            <div style={{
              position: 'absolute',
              bottom: '4px',
              right: '4px',
              background: '#26C3C0',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}>
              <input
                type="file"
                accept="image/*"
                onChange={handleProfilePicChange}
                style={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  opacity: 0,
                  cursor: 'pointer'
                }}
              />
              <EditIcon style={{ fontSize: '12px', color: '#fff' }} />
            </div>
          )}
        </div>

        {/* Profile Details */}
        <div style={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: '24px'
        }}>
          {/* Column 1 - Username */}
          <div>
            <div style={{ marginBottom: '16px' }}>
              <div style={{
                fontSize: '14px',
                color: mode === 'dark' ? '#B0B0B0' : '#666',
                marginBottom: '4px',
                fontFamily: 'Urbanist, Arial, sans-serif'
              }}>
                Username:
              </div>
                  {isEditMode ? (
                    <div>
                      <TextField
                        value={editData.username || ''}
                        onChange={(e) => handleInputChange('username', e.target.value)}
                        variant="outlined"
                        size="small"
                        fullWidth
                        error={showValidation && !!formErrors.username}
                        helperText={showValidation && formErrors.username}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '6px',
                            fontFamily: 'Urbanist, Arial, sans-serif',
                            fontSize: '16px',
                            '& fieldset': {
                              borderColor: mode === 'dark' ? '#444' : '#ddd',
                            },
                            '&:hover fieldset': {
                              borderColor: '#26C3C0',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#26C3C0',
                            },
                          },
                          '& .MuiInputBase-input': {
                            color: mode === 'dark' ? '#fff' : '#333',
                            fontWeight: '600',
                          },
                          '& .MuiFormHelperText-root': {
                            color: '#f44336',
                            fontSize: '12px',
                            marginTop: '4px',
                          },
                        }}
                      />
                    </div>
                  ) : (
                <div style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: mode === 'dark' ? '#fff' : '#333',
                  fontFamily: 'Urbanist, Arial, sans-serif'
                }}>
                  {currentUserData.username || ''}
                </div>
              )}
            </div>
          </div>

          {/* Column 2 - Full Name */}
          <div>
            <div style={{ marginBottom: '16px' }}>
              <div style={{
                fontSize: '14px',
                color: mode === 'dark' ? '#B0B0B0' : '#666',
                marginBottom: '4px',
                fontFamily: 'Urbanist, Arial, sans-serif'
              }}>
                Full Name:
              </div>
                  {isEditMode ? (
                    <div>
                      <TextField
                        value={editData.full_name || ''}
                        onChange={(e) => handleInputChange('full_name', e.target.value)}
                        variant="outlined"
                        size="small"
                        fullWidth
                        error={showValidation && !!formErrors.full_name}
                        helperText={showValidation && formErrors.full_name}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '6px',
                            fontFamily: 'Urbanist, Arial, sans-serif',
                            fontSize: '16px',
                            '& fieldset': {
                              borderColor: mode === 'dark' ? '#444' : '#ddd',
                            },
                            '&:hover fieldset': {
                              borderColor: '#26C3C0',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#26C3C0',
                            },
                          },
                          '& .MuiInputBase-input': {
                            color: mode === 'dark' ? '#fff' : '#333',
                            fontWeight: '600',
                          },
                          '& .MuiFormHelperText-root': {
                            color: '#f44336',
                            fontSize: '12px',
                            marginTop: '4px',
                          },
                        }}
                      />
                    </div>
                  ) : (
                <div style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: mode === 'dark' ? '#fff' : '#333',
                  fontFamily: 'Urbanist, Arial, sans-serif'
                }}>
                  {currentUserData.full_name || ''}
                </div>
              )}
            </div>
          </div>

          {/* Column 3 - Email */}
          <div>
            <div style={{ marginBottom: '16px' }}>
              <div style={{
                fontSize: '14px',
                color: mode === 'dark' ? '#B0B0B0' : '#666',
                marginBottom: '4px',
                fontFamily: 'Urbanist, Arial, sans-serif'
              }}>
                Email:
              </div>
                  {isEditMode ? (
                    <div>
                      <TextField
                        value={editData.email || ''}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        variant="outlined"
                        size="small"
                        fullWidth
                        type="email"
                        error={showValidation && !!formErrors.email}
                        helperText={showValidation && formErrors.email}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '6px',
                            fontFamily: 'Urbanist, Arial, sans-serif',
                            fontSize: '16px',
                            '& fieldset': {
                              borderColor: mode === 'dark' ? '#444' : '#ddd',
                            },
                            '&:hover fieldset': {
                              borderColor: '#26C3C0',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#26C3C0',
                            },
                          },
                          '& .MuiInputBase-input': {
                            color: mode === 'dark' ? '#fff' : '#333',
                            fontWeight: '600',
                          },
                          '& .MuiFormHelperText-root': {
                            color: '#f44336',
                            fontSize: '12px',
                            marginTop: '4px',
                          },
                        }}
                      />
                    </div>
                  ) : (
                <div style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: mode === 'dark' ? '#fff' : '#333',
                  fontFamily: 'Urbanist, Arial, sans-serif'
                }}>
                  {currentUserData.email || ''}
                </div>
              )}
            </div>
          </div>

          {/* Column 1 - Birthdate */}
          <div>
            <div style={{ marginBottom: '16px' }}>
              <div style={{
                fontSize: '14px',
                color: mode === 'dark' ? '#B0B0B0' : '#666',
                marginBottom: '4px',
                fontFamily: 'Urbanist, Arial, sans-serif'
              }}>
                Birthdate:
              </div>
              {isEditMode ? (
                    <div>
                      <TextField
                        value={editData.birthdate || ''}
                        onChange={(e) => handleInputChange('birthdate', e.target.value)}
                        variant="outlined"
                        size="small"
                        fullWidth
                        type="date"
                        error={showValidation && !!formErrors.birthdate}
                        helperText={showValidation && formErrors.birthdate}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '6px',
                            fontFamily: 'Urbanist, Arial, sans-serif',
                            fontSize: '16px',
                            '& fieldset': {
                              borderColor: mode === 'dark' ? '#444' : '#ddd',
                            },
                            '&:hover fieldset': {
                              borderColor: '#26C3C0',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#26C3C0',
                            },
                          },
                          '& .MuiInputBase-input': {
                            color: mode === 'dark' ? '#fff' : '#333',
                            fontWeight: '600',
                          },
                          '& .MuiSvgIcon-root': {
                            color: mode === 'dark' ? '#fff' : '#666',
                          },
                          '& .MuiInputAdornment-root .MuiSvgIcon-root': {
                            color: mode === 'dark' ? '#fff' : '#666',
                          },
                          '& .MuiIconButton-root .MuiSvgIcon-root': {
                            color: mode === 'dark' ? '#fff' : '#666',
                          },
                          '& .MuiInputAdornment-root': {
                            color: mode === 'dark' ? '#fff' : '#666',
                          },
                          '& .MuiIconButton-root': {
                            color: mode === 'dark' ? '#fff' : '#666',
                          },
                          '& .MuiIconButton-root:hover': {
                            color: mode === 'dark' ? '#fff' : '#666',
                          },
                          // Target the date picker icon specifically
                          '& input[type="date"]::-webkit-calendar-picker-indicator': {
                            filter: mode === 'dark' ? 'invert(1)' : 'invert(0)',
                            cursor: 'pointer',
                          },
                          '& input[type="date"]::-webkit-inner-spin-button': {
                            filter: mode === 'dark' ? 'invert(1)' : 'invert(0)',
                          },
                          '& .MuiFormHelperText-root': {
                            color: '#f44336',
                            fontSize: '12px',
                            marginTop: '4px',
                          },
                        }}
                      />
                    </div>
                  ) : (
                <div style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: mode === 'dark' ? '#fff' : '#333',
                  fontFamily: 'Urbanist, Arial, sans-serif'
                }}>
                  {currentUserData.birthdate || ''}
                </div>
              )}
            </div>
          </div>

          {/* Column 2 - Empty */}
          <div>
          </div>

          {/* Column 3 - Empty */}
          <div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
